import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { UiIcon } from "../Icon";
import HelloBar from "../HelloBar";
import {
  ICON_LIST_PATH,
  EXAMPLES_PATH,
  ABOUT_PATH,
  COMPOSER_PATH,
  STUDIO_PATH,
  LICENSE_PATH,
} from "../../constants";
import { fetchSelectedIcons } from "../../Actions/iconAction";
import { readCustomIcons, onCustomIconsChange } from "../../utils/customIcons";

// Either side of the logo (the logo itself goes home)
const START_LINKS = [
  { to: ICON_LIST_PATH, label: "Icons" },
  { to: COMPOSER_PATH, label: "Composer" },
  { to: STUDIO_PATH, label: "Studio", tag: "New" },
];
const END_LINKS = [
  { to: EXAMPLES_PATH, label: "Examples" },
  { to: ABOUT_PATH, label: "About" },
  { to: LICENSE_PATH, label: "License" },
];

// Logo in the middle with the menu split either side; on small screens both halves open
// from a button as one dropdown
export const Navbar = ({ location, dispatch, pickedCount, showHelloBar, className = "" }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [customCount, setCustomCount] = useState(0);

  // The composer selection lives in the browser, so it's read after the first render.
  // Studio icons added to the font count too, and follow changes from any tab
  useEffect(() => {
    dispatch(fetchSelectedIcons());
    setCustomCount(readCustomIcons().length);
    return onCustomIconsChange((list) => setCustomCount(list.length));
  }, []);

  // How many icons are in your font, shown on the Composer item once there are any
  const fontCount = pickedCount + customCount;

  // The menu closes when you go to another page
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // ...and on Esc or a click outside it
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !event.target.closest(".site-header__nav, .site-header__toggle")) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  return (
    <section className={className}>
      <header>
        {showHelloBar ? <HelloBar pathname={location.pathname} /> : null}
        <div className="site-header">
          <Link className="site-header__logo" to="/" aria-label="IcoFont home">
            <img src="/images/logo.svg" alt="IcoFont" />
          </Link>
          <button
            type="button"
            className="site-header__toggle"
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <UiIcon name={menuOpen ? "close-line" : "navigation-menu"} />
          </button>
          <nav id="site-menu" className={`site-header__nav${menuOpen ? " is-open" : ""}`} aria-label="Main">
            {[START_LINKS, END_LINKS].map((links, index) => (
              <ul key={index} className={`site-header__menu site-header__menu--${index ? "end" : "start"}`}>
                {links.map((link) => {
                  const active = location.pathname === link.to;
                  const count = link.to === COMPOSER_PATH ? fontCount : 0;
                  return (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className={active ? "active" : ""}
                        aria-current={active ? "page" : undefined}
                        aria-label={count ? `${link.label}, ${count} ${count === 1 ? "icon" : "icons"} in your font` : undefined}
                      >
                        {link.label}
                        {link.tag ? <span className="site-header__tag">{link.tag}</span> : null}
                        {count ? (
                          <span className="site-header__count" aria-hidden="true">
                            {count}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ))}
          </nav>
        </div>
      </header>
    </section>
  );
};

Navbar.propTypes = {
  location: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  pickedCount: PropTypes.number,
  showHelloBar: PropTypes.bool,
  className: PropTypes.string,
};

export default connect((store) => ({
  pickedCount: (store.iconstore.iconstore.selected_glyphs || []).length,
}))(Navbar);
